/**
 * GameScoreboardPage — May 2 2026
 *
 * Standalone live scoreboard for a single game. Per user request,
 * the live ScoreboardCard was removed from the dashboard hero
 * (where it was distracting) and from the GamePage (where it was
 * buried mid-scroll). Now the scoreboard lives at its own URL,
 * and DashboardPage / GamePage link to it via a "Live Scoreboard →"
 * button.
 *
 * Route: /team/:teamId/games/:gameId/scoreboard
 *
 * The page itself is just chrome around the existing
 * ScoreboardCard — that component already handles fetching,
 * permissions, websocket subscription, optimistic updates, and
 * the "feature is off" empty state. We add a header with game
 * date / arena context so users know which game's scoreboard
 * they're looking at without bouncing back.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Trophy, MapPin, Clock, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTimeFormat } from '../hooks/useTimeFormat.js';
import ScoreboardCard from '../components/shared/ScoreboardCard.jsx';
import { PageSpinner } from '../components/Skeleton.jsx';
import { useWakeLock } from '../hooks/useWakeLock.js';

export default function GameScoreboardPage() {
  const { teamId, gameId } = useParams();
  // v2.5.186 — keep the screen awake while running the live scoreboard
  // (coach's phone on the bench during a game). No-ops where unsupported.
  useWakeLock(true);
  const navigate = useNavigate();
  const { isCoach, isManager, role } = useTeamRole(teamId);
  const { user } = useAuth();
  // 2026-09-23 — coach, or a designated recorder (Team settings → Live stats).
  const [recorders, setRecorders] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/teams/${teamId}`).then(({ data }) => {
      if (live) setRecorders(Array.isArray(data?.settings?.stat_recorders) ? data.settings.stat_recorders : []);
    }).catch(() => { if (live) setRecorders([]); });
    return () => { live = false; };
  }, [teamId]);
  const canRecord = role === 'coach' || (!!user?.id && (recorders || []).includes(user.id));
  const { formatTime } = useTimeFormat();

  const [game, setGame] = useState(null);
  const [session, setSession] = useState(null); // {rootId, total, games:[{id,gameNumber,status,home,away,isCurrent}]}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // v2.5.88 — mini-game delete confirmation. `deleteTarget` is the
  // chip the coach tapped × on, or null. `deleting` debounces the
  // confirmation button so a second tap can't fire two requests.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // v2.5.92 — iOS-style Edit toggle. When false, chips render
  // clean (no × badges) and tap navigates to the chip's mini-game.
  // When true, an × button overlays each chip; tap opens the
  // delete confirmation modal. Pulled out of the chip-render path
  // because the always-visible × from v2.5.88 felt like an
  // unwanted notification badge competing with the chip border.
  // Defaults to false on every page mount — coaches who want to
  // delete need to tap Edit first, which is the right amount of
  // friction for a destructive action.
  const [editMode, setEditMode] = useState(false);

  // Single fetch covers both the game header AND the session list.
  // Run in parallel so the page paints fast even on slow networks.
  // Both endpoints are independently failure-tolerant.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/teams/${teamId}/games/${gameId}`).catch(err => ({ _err: err })),
      api.get(`/teams/${teamId}/games/${gameId}/session`).catch(() => ({ data: null })),
    ]).then(([gRes, sRes]) => {
      if (cancelled) return;
      if (gRes._err) {
        setError(gRes._err.response?.data?.error || 'Failed to load game');
      } else {
        setGame(gRes.data);
        setError(null);
      }
      setSession(sRes.data || null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [teamId, gameId]);

  // v2.5.88 — confirm + delete a mini-game. The endpoint enforces
  // session-integrity rules (can't delete the root if siblings exist;
  // must be a multi-game session; ignores status='completed'). On
  // success: if the deleted game was the one we're viewing, navigate
  // to the fallback game returned by the server. Otherwise refresh
  // the session strip in place.
  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/teams/${teamId}/games/${deleteTarget.id}/mini-game`);
      const fallbackGameId = res?.data?.fallbackGameId;
      const wasCurrent = deleteTarget.id === gameId;
      toast.success(`Mini ${deleteTarget.gameNumber} deleted`);
      setDeleteTarget(null);
      // v2.5.92 — exit edit mode after a successful delete. The
      // chip the coach was editing is gone; staying in edit mode
      // would leave them looking at a depleted strip with × badges
      // on the survivors, which feels stuck. Dropping out resets
      // the page to its default reading state.
      setEditMode(false);
      if (wasCurrent && fallbackGameId) {
        // We deleted the page we're on. Replace history entry so the
        // back button doesn't take the user back to a 404.
        navigate(`/team/${teamId}/games/${fallbackGameId}/scoreboard`, { replace: true });
      } else if (wasCurrent) {
        // No fallback (shouldn't happen — backend wouldn't allow
        // deletion of a sole-game session — but defensive). Go back.
        navigate(-1);
      } else {
        // Refresh the session strip in place.
        const sRes = await api.get(`/teams/${teamId}/games/${gameId}/session`).catch(() => ({ data: null }));
        setSession(sRes.data || null);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to delete mini-game';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // v2.5.88 — body scroll lock while the delete confirmation modal is
  // open. Matches the pattern in GamePage's reusable Modal component
  // (line ~6878). Without this, mouse wheel on desktop can scroll
  // the underlying page behind the dim layer; on iOS Safari the
  // page can still rubber-band-scroll. Restored on unmount or when
  // the modal closes.
  useEffect(() => {
    if (!deleteTarget) return;
    const prevOverflow   = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow   = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow   = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [deleteTarget]);

  if (loading) return <PageSpinner />;

  // Session has 2+ games → show the strip. Single-game (no parent + no
  // siblings) renders without it for visual cleanliness.
  const isMultiSession = session && session.total > 1;
  const currentMiniNumber = session?.games.find(g => g.isCurrent)?.gameNumber;

  return (
    <div className="animate-fade-in">
      <BackBar onBack={() => navigate(-1)} />

      {/* flex-wrap: at ~390px the title + MINI badge can exceed the
          row width (long badge text, larger font-scaling a11y
          settings) — wrap instead of clipping/forcing page-wide
          horizontal scroll. No effect on desktop, where it fits. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Trophy size={20} style={{ color: 'var(--ice)' }} />
        <h1 className="section-title text-2xl">Live Scoreboard</h1>
        {isMultiSession && currentMiniNumber && (
          <span style={{
            background: 'rgba(0,212,255,0.12)',
            border: '1px solid rgba(0,212,255,0.35)',
            color: 'var(--ice)',
            padding: '3px 9px', borderRadius: 999,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
            fontFamily: 'var(--font-display)',
            marginLeft: 'auto',
          }}>
            MINI {currentMiniNumber} OF {session.total}
          </span>
        )}
      </div>

      {/* Game context — date, time, arena. Keeps users oriented since
          the page is reachable from multiple entry points (dashboard,
          game page, direct link). */}
      {game && (
        <div className="card mb-4" style={{ padding: 14 }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: 'var(--text-2)', textTransform: 'uppercase', marginBottom: 6 }}>
            {(() => {
              try { return format(parseISO(game.starts_at), 'EEEE, MMM d'); }
              catch { return ''; }
            })()}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--text-2)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={13} />
              {(() => {
                try { return formatTime(game.starts_at); }
                catch { return ''; }
              })()}
            </span>
            {game.arena_name && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={13} />{game.arena_name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sep 2026 — Phase 7C entry point. The coach keeps score here,
          so this is where recording has to start (the only other
          entry was the "Track live" link on the game page's stats
          card, which nobody found). Coach/manager only — the
          recorder page enforces the same rule. Target is the
          session root game when this is a mini-game session so one
          recording covers the whole night. */}
      {canRecord && (
        <button
          type="button"
          onClick={() => navigate(`/team/${teamId}/game/${session?.rootId || gameId}/live`)}
          className="w-full mb-4"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 16px', borderRadius: 14, textAlign: 'left',
            background: 'linear-gradient(135deg, rgba(239,68,68,0.16), rgba(139,92,246,0.14))',
            border: '1px solid rgba(239,68,68,0.45)',
            color: 'var(--text)', cursor: 'pointer',
          }}>
          <span style={{
            width: 40, height: 40, borderRadius: 999, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#ef4444', color: '#fff', fontSize: 20,
            boxShadow: '0 0 0 4px rgba(239,68,68,0.25)',
          }}>🎙</span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 15, letterSpacing: '0.02em' }}>
              Record &amp; track live
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              Call the game out loud — goals, assists and the score update for the team as you go.
            </span>
          </span>
        </button>
      )}

      {/* Mini-game session strip — shows all mini-games in this session
          as tappable chips with their final scores. Lets the coach
          jump back to mini-game 2 to fix a mistake without scrolling
          through schedule history. Only renders when there are 2+
          games in the session.

          v2.5.92 — visual rework. The previous design (v2.5.88) put
          a small × notification-badge on every chip permanently for
          coaches/managers, which felt visually noisy and competed
          with the chip border. The destructive action also shouldn't
          be the second-most-prominent thing on every chip just to
          accommodate a rare-use case.

          New design follows the iOS edit-mode convention. By default
          the strip looks clean — the chip is the chip, no badges. A
          subtle "Edit" text button sits on the section header for
          coaches/managers; tapping it reveals × badges on each chip
          with a gentle wiggle animation, and replaces "Edit" with
          "Done." Tapping × prompts the confirmation modal. Tapping
          "Done" returns the strip to its clean state. */}
      {isMultiSession && (
        <div className="card mb-4" style={{ padding: 12 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, marginBottom: 8,
          }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--text-2)', textTransform: 'uppercase', margin: 0 }}>
              Session mini-games
            </p>
            {(isCoach || isManager) && session.games.length >= 2 && (
              // v2.5.92 — the Edit/Done toggle. Only rendered for
              // coaches/managers (gates the destructive action) and
              // only when there's actually something to edit (2+
              // games — single-game sessions can't lose mini's via
              // this UI; deletion of a 1-game would need to use the
              // regular game delete from Schedule). Text-button
              // styling rather than a colored pill — keeps the
              // section header reading as a header.
              <button
                type="button"
                onClick={() => setEditMode(v => !v)}
                disabled={deleting}
                style={{
                  appearance: 'none',
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 8px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                  color: editMode ? 'var(--ice)' : 'var(--text-2)',
                  cursor: deleting ? 'wait' : 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}>
                {editMode ? 'Done' : 'Edit'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {session.games.map(g => {
              const hasScore = g.home != null && g.away != null;
              const scoreText = hasScore ? `${g.home}–${g.away}` : '0–0';
              const isComplete = g.status === 'completed';
              const canDeleteMini = isCoach || isManager;
              // v2.5.92 — × badge is rendered only when editMode is on.
              // Wiggle animation is a tiny tilt-back-and-forth keyed
              // on the chip id so each chip's wiggle is offset slightly
              // (parity with the iOS home-screen edit mode), set up via
              // inline keyframes injected once below.
              const showDelete = canDeleteMini && editMode;
              return (
                <div key={g.id} style={{
                  position: 'relative',
                  // Subtle wiggle when in edit mode. Disabled for the
                  // current chip (the one the coach is viewing) since
                  // wiggling-while-disabled feels broken. Each chip
                  // gets a slightly offset animation-delay derived from
                  // its id hash so the wiggles don't all march in
                  // lockstep — that's what makes the iOS effect feel
                  // organic.
                  animation: showDelete && !g.isCurrent ? 'mini-wiggle 0.55s ease-in-out infinite' : 'none',
                  animationDelay: showDelete ? `${(g.id?.charCodeAt(0) || 0) % 7 * 60}ms` : '0ms',
                  transformOrigin: 'center center',
                }}>
                  <button
                    type="button"
                    onClick={() => { if (!g.isCurrent && !editMode) navigate(`/team/${teamId}/games/${g.id}/scoreboard`); }}
                    disabled={g.isCurrent || (editMode && !canDeleteMini)}
                    style={{
                      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                      gap: 2,
                      padding: '6px 10px',
                      borderRadius: 10,
                      background: g.isCurrent ? 'rgba(0,212,255,0.15)' : 'var(--surface-2)',
                      border: g.isCurrent ? '1.5px solid var(--ice)' : '1px solid var(--border)',
                      color: g.isCurrent ? 'var(--ice)' : 'var(--text)',
                      // In edit mode, the chip's primary action becomes
                      // "tap × to delete," not "tap chip to navigate."
                      // Cursor reflects that — chip itself becomes
                      // default-cursor since tapping the chip does
                      // nothing while editing.
                      cursor: g.isCurrent ? 'default' : (editMode ? 'default' : 'pointer'),
                      minWidth: 56,
                      // Slight de-emphasis in edit mode: the chip sits
                      // back so the × badge reads as the primary
                      // affordance. Just opacity — still legible.
                      opacity: editMode && !g.isCurrent ? 0.85 : 1,
                      transition: 'opacity 0.15s ease',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      color: g.isCurrent ? 'var(--ice)' : 'var(--text-2)',
                    }}>
                      Mini {g.gameNumber}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                      {scoreText}
                    </span>
                    {/* 2026-07-10 (batch-B E1, per Gilles) — the viewer's
                        own result for each completed mini, computed
                        server-side from THEIR game_rosters assignment
                        (session endpoint returns myOutcome). Coaches who
                        played get it too; unassigned viewers just see
                        FINAL as before. */}
                    {isComplete && g.myOutcome ? (
                      <span style={{
                        fontSize: 8, fontWeight: 800, letterSpacing: '0.12em',
                        color: g.myOutcome === 'won' ? 'var(--goal)'
                             : g.myOutcome === 'lost' ? 'var(--penalty, #f87171)'
                             : 'var(--text-3)',
                      }}>
                        {g.myOutcome === 'won' ? 'YOU WON' : g.myOutcome === 'lost' ? 'YOU LOST' : 'TIE'}
                      </span>
                    ) : isComplete && (
                      <span style={{ fontSize: 8, color: 'var(--text-3)', letterSpacing: '0.12em' }}>FINAL</span>
                    )}
                  </button>
                  {/* v2.5.92 — × badge. Rendered only in edit mode,
                      positioned at the TOP-LEFT (iOS convention —
                      keeps the badge clear of the score numerals
                      which sit center-right). Slightly larger than
                      v2.5.88's top-right placement (20px vs 18px) so
                      it's tappable on small phones, and uses a solid
                      penalty-color fill with white × for unmistakable
                      "this deletes" semantics. */}
                  {showDelete && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(g);
                      }}
                      aria-label={`Delete Mini ${g.gameNumber}`}
                      title={`Delete Mini ${g.gameNumber}`}
                      disabled={deleting}
                      style={{
                        position: 'absolute',
                        // Sit just outside the top-left corner of the
                        // chip so the badge feels detached and clearly
                        // belongs to the chip beneath it (iOS pattern).
                        top: -6, left: -6,
                        width: 20, height: 20,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%',
                        background: 'var(--penalty, #e34a4a)',
                        border: '1.5px solid var(--surface)',
                        color: '#fff',
                        padding: 0,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.18)',
                        cursor: deleting ? 'wait' : 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                        // Counter-rotate the badge so it doesn't
                        // wiggle along with the chip — keeps the ×
                        // visually crisp even while the parent
                        // animates. animation: none alone wouldn't
                        // suffice because transform composition
                        // would still be inherited; explicit
                        // animation-name override breaks the link.
                        animation: 'none',
                      }}>
                      <X size={12} strokeWidth={2.75} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {/* v2.5.92 — keyframes for the wiggle animation. Inlined as
              a <style> tag inside the strip so the animation only
              ships when the strip renders (no global CSS bloat) and
              we don't have to touch the index.css file just for one
              transient effect. The motion is intentionally subtle —
              ±1.2deg rotation — enough to read as "you're in edit
              mode" without being distracting. */}
          {editMode && (
            <style>{`
              @keyframes mini-wiggle {
                0%   { transform: rotate(-1.2deg); }
                50%  { transform: rotate(1.2deg); }
                100% { transform: rotate(-1.2deg); }
              }
              @media (prefers-reduced-motion: reduce) {
                /* Respect users who've opted out of motion. The ×
                   badges still render so the feature works — the
                   chips just don't tilt. */
                @keyframes mini-wiggle {
                  0%, 100% { transform: none; }
                }
              }
            `}</style>
          )}
        </div>
      )}

      {error && (
        <div className="card mb-4" style={{ padding: 14, borderColor: 'var(--penalty)' }}>
          <p className="text-sm" style={{ color: 'var(--penalty)' }}>{error}</p>
        </div>
      )}

      {/* The actual scoreboard — self-contained component.
          Handles its own permission gating, websocket subscription,
          and the "scoreboard disabled" empty state.
          May 2 2026: onSavedAndNew callback navigates the coach to
          the freshly-created sibling mini-game's scoreboard so they
          can immediately start scoring it. */}
      {teamId && gameId && (
        <ScoreboardCard
          teamId={teamId}
          gameId={gameId}
          isCoach={isCoach}
          isManager={isManager}
          onSavedAndNew={(newGameId) => {
            // 2026-07-10 (batch-B A6, coach bug report) — replace, don't
            // push: pushing meant the Back button returned to the just-
            // locked PRIOR mini-game instead of where the coach came
            // from. The session chip strip still jumps between minis.
            navigate(`/team/${teamId}/games/${newGameId}/scoreboard`, { replace: true });
          }}
        />
      )}

      {/* Mobile-only buffer so the scoreboard sits clear of the bottom nav. */}
      <div className="sm:hidden" aria-hidden style={{ height: 70 }} />

      {/* v2.5.88 — Mini-game delete confirmation modal. Light-weight
          inline modal: dim overlay + centered card + Cancel/Delete
          buttons. Esc closes; Enter confirms (matches the existing
          per-team Reset Connect pattern from v2.5.82). The score
          line re-states what's about to be lost so the coach
          double-checks before tapping Delete. Uses --mobile-h for
          the wrapper height (per v2.5.86) so it doesn't get pinned
          behind the iOS keyboard — though there's no input here,
          consistency with other modals avoids the regression. */}
      {deleteTarget && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) setDeleteTarget(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !deleting) setDeleteTarget(null);
            // v2.5.88 — Enter only confirms when the keydown happened
            // ON the wrapper itself (i.e., focus is on the wrapper, no
            // button has focus). If a button is focused, the browser
            // already turns Enter into a synthetic click on that
            // button — handling Enter here too would BOTH click the
            // button AND fire handleConfirmDelete from the bubbled
            // keydown. For the Cancel button case that's catastrophic:
            // the Cancel onClick clears deleteTarget but the queued
            // handleConfirmDelete reads the *still-truthy* closure
            // value and deletes the game anyway. e.target === e.
            // currentTarget filters that out cleanly.
            if (e.key === 'Enter' && e.target === e.currentTarget && !deleting) handleConfirmDelete();
          }}
          style={{
            position: 'fixed', left: 0, right: 0, top: 0,
            height: 'var(--mobile-h, 100dvh)',
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, zIndex: 500,
            touchAction: 'none',
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 14,
              padding: 20,
              maxWidth: 380, width: '100%',
              maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.85)',
              overflowY: 'auto',
              boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
            }}>
            <h3 style={{
              margin: 0, marginBottom: 8,
              fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
              color: 'var(--text)',
            }}>
              Delete Mini {deleteTarget.gameNumber}?
            </h3>
            <p style={{
              margin: 0, marginBottom: 6,
              fontSize: 13, lineHeight: 1.5, color: 'var(--text-2)',
            }}>
              This will permanently delete Mini {deleteTarget.gameNumber} and its score.
              Other mini-games in the session are not affected.
            </p>
            {deleteTarget.home != null && deleteTarget.away != null && (
              <p style={{
                margin: 0, marginBottom: 14,
                fontSize: 13, color: 'var(--text-2)',
              }}>
                Score that will be lost:{' '}
                <strong style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                  {deleteTarget.home}–{deleteTarget.away}
                </strong>
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {/* v2.5.88 — autoFocus on Cancel (not on a parent div,
                  which browsers ignore for autoFocus). With Cancel
                  focused on open, Esc bubbles to the wrapper's
                  onKeyDown and dismisses the modal; Enter triggers
                  Cancel's onClick (the wrapper's Enter handler bails
                  because target !== currentTarget). User has to
                  Tab once to focus Delete — a deliberate friction
                  that prevents accidental confirms from a
                  trigger-happy Enter press. */}
              <button
                type="button"
                autoFocus
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={{
                  padding: '9px 16px', borderRadius: 10,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-2)', cursor: deleting ? 'wait' : 'pointer',
                  fontSize: 13, fontWeight: 600,
                  WebkitTapHighlightColor: 'transparent',
                }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                style={{
                  padding: '9px 16px', borderRadius: 10,
                  background: 'rgba(255,68,68,0.10)',
                  border: '1px solid rgba(255,68,68,0.45)',
                  color: 'var(--penalty, #e34a4a)',
                  cursor: deleting ? 'wait' : 'pointer',
                  fontSize: 13, fontWeight: 700, letterSpacing: '0.02em',
                  WebkitTapHighlightColor: 'transparent',
                }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: 'var(--bg)', margin: '-12px -14px 12px',
      padding: '8px 14px 6px',
    }}>
      <button onClick={onBack} className="btn-ghost -ml-1"
        style={{ padding: '8px 12px', fontSize: 12, minHeight: 38, borderRadius: 10 }}>
        <ChevronLeft size={15} /> Back
      </button>
    </div>
  );
}
